import {
	CLASSIFICATIONS,
	ClassificationBadge,
	type Classification,
} from '#app/components/classification-badge.tsx'
import { EditorialList } from '#app/components/editorial-list.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { FaqList } from '#app/components/faq-list.tsx'
import { PageHero } from '#app/components/page-hero.tsx'
import { SectionHeader } from '#app/components/section-header.tsx'
import { SectionLayout } from '#app/components/section-layout.tsx'
import { Timeline } from '#app/components/timeline.tsx'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Frisbeegolfratojen luokitukset' },
	{
		name: 'description',
		content:
			'Suomen frisbeegolfratojen luokitusjärjestelmä: A-, B-, C- ja D-luokat, tasot 1–3 sekä usein kysytyt kysymykset.',
	},
]

const PAGE_ANCHORS = [
	{ href: '#yleiskuva', label: 'Yleiskuva' },
	{ href: '#tarkemmat', label: 'Tarkemmat luokat' },
	{ href: '#tasot', label: 'Tasot' },
	{ href: '#usein-kysyttya', label: 'Usein kysyttyä' },
]

export default function Luokitukset() {
	return (
		<>
			<PageHero
				eyebrow="Luokitukset"
				title="Frisbeegolfratojen luokitukset"
				intro="Suomen radat vaihtelevat muutaman korin pelipaikoista kilpatason kohteisiin. Luokitukset jakavat ne haastavuuden ja varustelutason mukaan, jotta löydät juuri sinulle sopivan radan."
			/>

			<SectionLayout anchors={PAGE_ANCHORS}>
				<OverviewSection />
				<TarkemmatSection />
				<TasotSection />
				<FaqSection />
			</SectionLayout>
		</>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Sections                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

const CLASS_ROWS = [
	{
		letter: 'A',
		sample: 'AAA1',
		eyebrow: 'AAA – A',
		title: 'Kilparadat',
		stats: ['18+ koria', '50–300 m väylät', 'par 54–70+'],
		body: 'Vähintään 18-väyläisiä. AAA-luokan radoilla väylät ovat erittäin pitkiä ja haastavuudeltaan vaativimpia, AA-taso on hieman edistyneemmille ja A-luokka soveltuu jopa aloittelijoille.',
	},
	{
		letter: 'B',
		sample: 'BB1',
		eyebrow: 'BB – B',
		title: 'Harrastajaradat',
		stats: ['9–17 koria', '50–150 m väylät', 'par 27–54'],
		body: 'Suomen yleisin rataluokitus. Soveltuvat kaikentasoisille pelaajille — kilpapelaajista aloittelijoihin. BB-luokituksen radoilla väylien keskimääräinen pituus on pidempi kuin B-luokan radoilla.',
	},
	{
		letter: 'C',
		sample: 'C2',
		eyebrow: 'C',
		title: 'Aloittelijaradat',
		stats: ['7+ koria', 'alle 75 m', 'puistorata'],
		body: 'Aloittelijoille soveltuvia tutustumis- ja pelipaikkoja. Väylät ovat lyhyitä, ja par-tulosta tai merkittyjä heittopaikkoja ei välttämättä ole. Yleensä pienemmillä alueilla, missä tilaa useammille väylille ei ole.',
	},
	{
		letter: 'D',
		sample: 'D1',
		eyebrow: 'D',
		title: 'Koululaisradat',
		stats: ['3–6 koria', 'alle 50 m', 'koulurata'],
		body: 'Koululaisratoja, jotka soveltuvat frisbeegolfin tutustumiseen sekä koulun liikuntatunneille. Kaikki D-luokan pelipaikat eivät välttämättä täytä SFL:n frisbeegolfradan minimiehtoja (väh. 6 koria).',
	},
]

function OverviewSection() {
	return (
		<section id="yleiskuva" className="scroll-mt-12">
			<SectionHeader number="01" title="Neljä ryhmää" />
			<div className="mt-8 md:mt-10">
				<EditorialList
					items={CLASS_ROWS.map((row) => ({
						mark: row.letter,
						eyebrow: row.eyebrow,
						title: row.title,
						stats: row.stats,
						accessory: <ClassificationBadge value={row.sample} size="lg" />,
						body: row.body,
					}))}
				/>
			</div>
		</section>
	)
}

const MATRIX_ROWS: {
	prefix: string
	description: string
	values: Classification[]
}[] = [
	{ prefix: 'AAA', description: 'Vaativimmat kilparadat, vähintään 18 väylää, pitkät väylät.' },
	{ prefix: 'AA', description: 'Pitkät kilpa- ja harrastajaradat, 18+ väylää.' },
	{ prefix: 'A', description: 'Täysimittaiset radat, 18+ väylää, soveltuu myös aloittelijoille.' },
	{ prefix: 'BB', description: 'Harrastajaradat, väh. 9 väylää, keskipituus väh. 100 m.' },
	{ prefix: 'B', description: 'Harrastajaradat, väh. 9 väylää, keskipituus väh. 75 m.' },
	{ prefix: 'C', description: 'Aloittelijaradat, väh. 7 koria, lyhyet väylät (alle 75 m).' },
	{ prefix: 'D', description: 'Koululaisradat, 3–6 koria, väylät alle 50 m.' },
].map((p) => ({
	...p,
	values: (CLASSIFICATIONS as readonly string[]).filter((v) =>
		new RegExp(`^${p.prefix}[123]$`).test(v),
	) as Classification[],
}))

function TarkemmatSection() {
	return (
		<section id="tarkemmat" className="border-border mt-20 scroll-mt-12 border-t pt-16">
			<SectionHeader number="02" title="Tarkemmat luokat" />
			<p className="text-foreground/85 mt-6 max-w-2xl text-base leading-relaxed">
				Radan haastavuus määräytyy puhtaasti numeroiden perusteella: väylien
				määrä, keskipituus ja par-luku. Luokittelussa ei oteta huomioon
				esimerkiksi korkeuseroja tai väylien avoimuutta. Numero (1–3)
				luokituksen perässä kuvaa varustelutasoa — katso{' '}
				<a className="text-primary underline" href="#tasot">
					Tasot
				</a>
				.
			</p>

			<dl className="divide-border border-border mt-8 divide-y border-y">
				{MATRIX_ROWS.map((row) => (
					<div
						key={row.prefix}
						className="grid grid-cols-1 gap-3 py-5 md:grid-cols-12 md:gap-6"
					>
						<dt className="md:col-span-2">
							<span className="text-foreground font-mono text-xl font-bold tracking-wider">
								{row.prefix}
							</span>
						</dt>
						<dd className="md:col-span-4">
							<div className="flex flex-wrap gap-1.5">
								{row.values.map((v) => (
									<ClassificationBadge key={v} value={v} size="sm" />
								))}
							</div>
						</dd>
						<dd className="text-foreground/75 text-sm leading-relaxed md:col-span-6">
							{row.description}
						</dd>
					</div>
				))}
			</dl>
		</section>
	)
}

const LEVELS = [
	{
		level: '1',
		sample: 'AAA1',
		title: 'Hyvin varusteltu rata',
		body: 'Viralliset kilpailukorit, opastejärjestelmä (väylän kartta, pituus, par, erikoisohjeet) ja päällystetyt heittopaikat (tekonurmi, betoni tai vastaava). Säännöllinen ylläpito — yritys, kunta tai paikallinen seura.',
	},
	{
		level: '2',
		sample: 'A2',
		title: 'Viralliset korit, merkityt heittopaikat',
		body: 'Viralliset maalikorit ja vähintään merkityt heittopaikat — päällystyksellä tai väyläopasteella, josta selviää yksiselitteisesti heittopaikka.',
	},
	{
		level: '3',
		sample: 'B3',
		title: 'Perustaso',
		body: 'Joko viralliset maalikorit, maalikorit tai maali-kohteet. Väylillä on merkityt heittopaikat (päällystys tai väyläopaste).',
	},
]

function TasotSection() {
	return (
		<section id="tasot" className="border-border mt-20 scroll-mt-12 border-t pt-16">
			<SectionHeader
				number="03"
				title="Tasot 1–3"
				subtitle="Luokituksen perässä oleva numero kertoo radan varustelu- ja ylläpitotasosta. Pienempi numero, parempi varustus."
			/>
			<div className="mt-10">
				<Timeline
					items={LEVELS.map((l) => ({
						marker: l.level,
						title: l.title,
						accessory: <ClassificationBadge value={l.sample} size="sm" />,
						body: l.body,
					}))}
				/>
			</div>

			<aside className="border-border text-muted-foreground mt-8 border-l-2 pl-4 text-sm leading-relaxed">
				<p>
					<strong className="text-foreground">Viralliset maalikorit</strong>{' '}
					ovat joko PDGA:n tai SFL:n hyväksymiä maalikoreja.
				</p>
				<p className="mt-2">
					<strong className="text-foreground">Opastejärjestelmä</strong>{' '}
					sisältää radan infotaulun sekä väyläkohtaiset opasteet.
				</p>
			</aside>
		</section>
	)
}

const FAQ = [
	{
		q: 'Miksi luokitukset?',
		a: 'Suomen frisbeegolfradat vaihtelevat suunnattomasti aina muutaman korin pelipaikoista kilpatason ratoihin. Rataluokitukset on perustettu parantamaan Suomen frisbeegolfratoja, niiden kehittämistä sekä arviointia. Luokitukset palvelevat lajin harrastajia ja ratojen ylläpitäjiä, ja helpottavat harrastajia valitsemaan uusia ratoja pelattavaksi.',
	},
	{
		q: 'Mitä luokitukset kertovat?',
		a: 'Rataluokitukset kertovat millaisesta frisbeegolfradasta on kysymys: väylien määrästä, varustelutasosta ja minkä tasoiselle harrastajalle rata on suunnattu. Luokitus ei kerro, onko rata metsärata vai puistorata — muut tiedot löytyvät ratojen profiilisivuilta.',
	},
	{
		q: 'Kuinka luokittelen radan?',
		a: 'Frisbeegolfratoja ei voi vielä luokitella itsenäisesti, vaan luokitukset myöntää ja hallinnoi frisbeegolfradat.fi. Mikäli kotiradallasi ei ole vielä rataluokitusta, ota yhteyttä meihin. Ratoja on joka lähtöön, ja osaa on hankalaa luokitella — nämä luokitellaan tapauskohtaisesti.',
	},
	{
		q: 'Mikä on DiscGolfPark?',
		a: (
			<>
				DiscGolfPark on Innovan frisbeegolfratatuote. Lisätietoa osoitteesta{' '}
				<a
					href="https://www.discgolfpark.net/fi/etusivu/"
					target="_blank"
					rel="noreferrer"
					className="text-primary underline"
				>
					discgolfpark.net
				</a>
				.
			</>
		),
	},
	{
		q: 'Huomasin virheen luokituksessa — mitä teen?',
		a: 'Otamme mielellämme vastaan palautetta. Mikäli huomaat, ettei jonkin radan luokitus vastaa todellisuutta, ota meihin yhteyttä.',
	},
]

function FaqSection() {
	return (
		<section id="usein-kysyttya" className="border-border mt-20 scroll-mt-12 border-t pt-16">
			<SectionHeader number="04" title="Usein kysyttyä" />
			<div className="mt-8">
				<FaqList items={FAQ} />
			</div>
		</section>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
